import { alpha, type Theme } from '@mui/material/styles';

import {
  clampSequenceIndex,
  getDataSequenceColorIndexForBreakdownLabel,
  UNCATEGORIZED_DATA_SEQUENCE_INDEX,
} from 'app-components/utils/theme/dataSequenceColors';

// should never be used, but here as a fallback exclusively to satisfy type demands
const colorFallback = '#cccccc';

// Cycles through sequence based on index, ignoring last color if reserved for uncategorized
export const getSafeColor = (colors: string[], index: number, dropLast?: boolean): string => {
  if (!colors || colors.length === 0) return colorFallback;
  return colors[Math.floor(Math.abs(index)) % (dropLast ? colors.length - 1 : colors.length)] || colorFallback;
};

export const isUncategorizedLabel = (label: string): boolean => label.trim().toLowerCase() === 'uncategorized';

/** The palette's neutral (gray) color, pulled from the last entry */
export const getUncategorizedColor = (colors: string[]): string => {
  if (!colors || colors.length === 0) return colorFallback;
  return colors[colors.length - 1] ?? colorFallback;
};

// used for category contexts where we want to 'pin' gray to uncategorized
export const getSequenceColorForLabel = (
  colors: string[],
  label: string,
  orderIndex: number,
  hasUncategorized: boolean,
): string => {
  if (isUncategorizedLabel(label)) return getUncategorizedColor(colors);
  return getSafeColor(colors, orderIndex, hasUncategorized);
};

// --- Data-sequence colors for a breakdown list ---
//
// Shared by the Spending dashboard's breakdown card and Reports' category comparison bar chart
// (SIMPL-35046). The index arithmetic lives in core (SIMPL-35049) because mobile's comparison rows
// have to pick the same entry out of their own tokens; what stays here is the web palette lookup.
export {
  getDataSequenceColorIndexForBreakdownLabel,
  isUncategorizedBreakdownLabel,
  UNCATEGORIZED_DATA_SEQUENCE_INDEX,
} from 'app-components/utils/theme/dataSequenceColors';

export function getUncategorizedDataSequenceColor(sequence: readonly string[]): string {
  return sequence[clampSequenceIndex(sequence.length, UNCATEGORIZED_DATA_SEQUENCE_INDEX)] ?? '';
}

export function getDataSequenceColorForBreakdownLabel(
  sequence: readonly string[],
  label: string,
  orderIndex: number,
): string {
  const index = getDataSequenceColorIndexForBreakdownLabel(sequence.length, label, orderIndex);
  return sequence[index] ?? sequence[0] ?? '';
}

// --- Comparison delta pill palette ---

/**
 * Every delta state that gets a pill, including the one with no direction.
 *
 * The source of truth for the delta vocabulary. Both React consumers narrow from it rather than
 * restating it — the dashboard's chip drops `no_spend_yet`, which it renders as a plain Chip — and
 * it is declared here rather than beside either of them because both import this file for the
 * palette, so reaching back the other way would be circular.
 */
export type ComparisonDeltaPillKind = 'increase' | 'decrease' | 'unchanged' | 'new_spend' | 'no_spend_yet';

export type ComparisonDeltaPillColors = Record<ComparisonDeltaPillKind, { background: string; color: string }>;

/**
 * The pill palette, resolved once from the theme.
 *
 * Exported because the comparison bar chart's tooltip pill is plain HTML — an ECharts formatter
 * cannot return React, so it cannot render a chip component. Two hand-maintained copies of these
 * five pairs would drift the moment one surface's colours were tuned, so every surface reads this:
 * the chart's tooltip pill, its delta column, and the Spending dashboard's chip.
 */
export function comparisonDeltaPillColors(theme: Theme): ComparisonDeltaPillColors {
  return {
    increase: { background: theme.palette.error.light, color: theme.palette.error.dark },
    decrease: { background: theme.palette.success.light, color: theme.palette.success.dark },
    unchanged: { background: theme.palette.action.selected, color: theme.palette.text.secondary },
    new_spend: { background: theme.palette.primary.light, color: theme.palette.primary.dark },
    no_spend_yet: { background: alpha(theme.palette.text.primary, 0.08), color: theme.palette.text.secondary },
  };
}

// --- Comparison delta sentiment palette ---

/**
 * How a delta *reads*, independent of which way the arrow points.
 *
 * The comparison chart separates the two on purpose: direction is category movement (did this
 * category grow?) while sentiment is whether that movement is good news. Income rising and expenses
 * rising are the same arrow and opposite colours, so a single `kind`-keyed palette — which is what
 * `comparisonDeltaPillColors` is — cannot express both.
 */
export type ComparisonDeltaSentiment = 'favorable' | 'unfavorable' | 'neutral';

export type ComparisonDeltaSentimentColors = Record<ComparisonDeltaSentiment, { background: string; color: string }>;

/**
 * The sentiment palette from the comparison-chart design.
 *
 * Uses `palette.amount.positive` / `.negative` for the text rather than `success.dark` /
 * `error.dark`, matching the reports grid's amount cells — the chip sits inches from them.
 */
export function comparisonDeltaSentimentColors(theme: Theme): ComparisonDeltaSentimentColors {
  return {
    favorable: { background: theme.palette.success.light, color: theme.palette.amount.positive },
    unfavorable: { background: theme.palette.error.light, color: theme.palette.amount.negative },
    neutral: { background: theme.palette.background.default, color: theme.palette.text.secondary },
  };
}
